// One house on the Diplomacy tab: where you stand with them, and the next rung.
import type { PlayerHudEntry } from '@aod/ui';
import { useState } from 'react';
import {
  type Diplomacy,
  nextRung,
  offerFrom,
  offerSent,
  pointsOf,
  relationBetween,
  warHonorCost,
} from '../../dynasty/diplomacy.js';
import { useT } from '../../i18n/index.js';
import type { TreatyKind } from '../../sim/useSimWorld/useTreatyIntents.js';

export function TreatyRow({
  player,
  diplomacy,
  localPlayerIndex,
  onPropose,
  onAnswer,
  onDeclareWar,
  married = false,
}: {
  readonly player: PlayerHudEntry;
  readonly diplomacy: Diplomacy;
  readonly localPlayerIndex: number;
  readonly onPropose?: (toPlayer: number, treaty: TreatyKind) => void;
  readonly onAnswer?: (fromPlayer: number, accept: boolean) => void;
  readonly onDeclareWar?: (toPlayer: number) => void;
  /** A marriage binds the two houses: breaking the alliance costs more. */
  readonly married?: boolean;
}) {
  const t = useT();
  const [arming, setArming] = useState(false);
  const other = player.playerId;
  const relation = relationBetween(diplomacy, localPlayerIndex, other);
  const incoming = offerFrom(diplomacy, localPlayerIndex, other);
  const rung = nextRung(relation);
  const affordable = pointsOf(diplomacy, localPlayerIndex) >= diplomacy.actionCost;
  const button =
    'rounded border border-border/60 px-2 py-0.5 text-[11px] text-foreground hover:bg-muted';
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-xs"
      data-testid={`treaty-row-${other}`}
    >
      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        {relation === 'neutral' ? t('diplomacy.neutral') : t(`treaty.${relation}`)}
      </span>
      {incoming ? (
        <>
          <span className="w-full text-[11px] leading-snug">
            {t('treaty.offerTitle', {
              house: player.name,
              treaty: t(`treaty.of.${incoming.state}`),
            })}
          </span>
          <button type="button" className={button} onClick={() => onAnswer?.(other, true)}>
            {t('treaty.accept')}
          </button>
          <button type="button" className={button} onClick={() => onAnswer?.(other, false)}>
            {t('treaty.decline')}
          </button>
        </>
      ) : offerSent(diplomacy, localPlayerIndex, other) ? (
        <span className="text-[10px] text-muted-foreground">{t('treaty.sent')}</span>
      ) : rung && onPropose ? (
        <button
          type="button"
          className={button}
          disabled={!affordable}
          title={affordable ? undefined : (t('treaty.noPoints') as string)}
          onClick={() => onPropose(other, rung)}
        >
          {t(`treaty.propose.${rung}`)}
        </button>
      ) : null}
      {onDeclareWar && relation !== 'war' && relation !== 'federation' && !incoming ? (
        arming ? (
          <>
            <button
              type="button"
              className={`${button} border-red-400/60 text-red-200`}
              onClick={() => {
                setArming(false);
                onDeclareWar(other);
              }}
            >
              {t('treaty.war.confirm', { honor: warHonorCost(diplomacy, relation, married) })}
            </button>
            <button type="button" className={button} onClick={() => setArming(false)}>
              {t('treaty.war.cancel')}
            </button>
          </>
        ) : (
          <button type="button" className={button} onClick={() => setArming(true)}>
            {t('treaty.war.declare')}
          </button>
        )
      ) : null}
    </div>
  );
}
